import type { DealType } from "./Deal";

export type DealDashboard = {
	dealApplicationTypeInfo: {
		acceptedCancelCount: number;
		draftCount: number;
		requestCount: number;
		requestedCount: number;
		waitingCancelCount: number;
	};
	dealStatusInfo: {
		awaitingAcceptanceCount: number;
		finishedCount: number;
		inConstructionCount: number;
		inEstimationCount: number;
		nowAcceptingCount: number;
		sendEstimateCount: number;
	};
	dealTypeInfo: {
		otherCount: number;
		repairCount: number;
		restorationCount: number;
	};
};

export type CompanyDealDashboard = DealDashboard & {
	dealTodoInfo: {
		dueInThreeDaysCount: number;
		dueOverCount: number;
		dueTodayCount: number;
		incompleteCount: number;
		unassignedCount: number;
	};
};

export type DealDashboardCondition = {
	authId?: number;
	dealTypes?: DealType[];
};

// 案件タイプのクエリパラメータを生成する
const addDealTypeParams = (
	params: URLSearchParams,
	dealTypes: DealType[]
): void => {
	if (dealTypes.length !== ["restoration", "repair", "other"].length) {
		for (const type of dealTypes) {
			params.append("deal_types[]", type);
		}
	}
};

// 店舗IDのクエリパラメータを追加する
const addAuthIdParam = (
	params: URLSearchParams,
	authId: number,
	masterAccount: boolean
): void => {
	if (masterAccount) {
		params.append("auth_id", String(authId));
	}
};

// 案件ステータスと案件タイプでフィルタされたクエリパラメータを生成する
export const dealStatusQueryParam = (
	statusIds: string[],
	dealTypes: DealType[],
	authId: number,
	masterAccount: boolean
): string => {
	const params = new URLSearchParams();
	for (const id of statusIds) {
		params.append("status_id[]", id);
	}
	addDealTypeParams(params, dealTypes);
	addAuthIdParam(params, authId, masterAccount);
	return params.toString();
};

export const vendorDealStatusQueryParam = (
	statusIds: string[],
	dealTypes: DealType[]
): string => {
	const params = new URLSearchParams();
	for (const id of statusIds) {
		params.append("status_id[]", id);
	}
	addDealTypeParams(params, dealTypes);
	return params.toString();
};

// 案件の申請状況と案件タイプでフィルタされたクエリパラメータを生成する
export const dealApplicationStatusQueryParam = (
	applicationType:
		| "requestEstimation"
		| "requestEstimationByCompany"
		| "requestEstimationByVendor"
		| "draftEstimationByCompany"
		| "draftEstimation"
		| "cancel",
	dealTypes: DealType[],
	authId: number,
	masterAccount: boolean
): string => {
	const params = new URLSearchParams();
	params.append("application_types[]", applicationType);
	addDealTypeParams(params, dealTypes);
	addAuthIdParam(params, authId, masterAccount);
	return params.toString();
};

export const vendorDealApplicationStatusQueryParam = (
	applicationType:
		| "requestEstimation"
		| "requestEstimationByCompany"
		| "requestEstimationByVendor"
		| "draftEstimationByCompany"
		| "draftEstimation"
		| "cancel",
	dealTypes: DealType[]
): string => {
	const params = new URLSearchParams();
	params.append("application_types[]", applicationType);
	addDealTypeParams(params, dealTypes);
	return params.toString();
};

// 案件タイプでフィルタされたクエリパラメータを生成する
export const dealTypeQueryParam = (
	dealType: DealType,
	authId: number,
	masterAccount: boolean
): string => {
	const params = new URLSearchParams();
	params.append("deal_types[]", dealType);
	addAuthIdParam(params, authId, masterAccount);
	return params.toString();
};

// TODOのクエリパラメータを生成する
export const todoQueryParam = (
	options: {
		completed?: boolean;
		dueDateFrom?: string;
		dueDateTo?: string;
		staffId?: string;
	},
	authId: number,
	masterAccount: boolean
): string => {
	const params = new URLSearchParams();
	Object.entries(options).forEach(([key, value]) => {
		if (value !== undefined) {
			params.append(key, String(value));
		}
	});
	if (masterAccount) {
		params.append("authId", String(authId));
	}
	return params.toString();
};
